import type { DataProvider } from "react-admin";

import { API_URL } from "./config";

async function request<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<{ data: T; headers: Headers }> {
  const headers: HeadersInit = { Accept: "application/json", ...(init?.headers as Record<string, string> | undefined) };
  let body: BodyInit | undefined = init?.body as BodyInit | undefined;
  if (init?.json !== undefined) {
    body = JSON.stringify(init.json);
    (headers as Record<string, string>)["Content-Type"] = "application/json";
  }
  const r = await fetch(API_URL + path, { ...init, headers, body, credentials: "include" });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    const err = new Error(text || `${r.status}`) as Error & { status?: number };
    err.status = r.status;
    throw err;
  }
  const data = r.status === 204 ? (null as unknown as T) : ((await r.json()) as T);
  return { data, headers: r.headers };
}

function buildListQuery(resource: string, params: import("react-admin").GetListParams): string {
  const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
  const { field, order } = params.sort ?? { field: "id", order: "ASC" };
  const start = (page - 1) * perPage;
  const end = start + perPage;
  const qs = new URLSearchParams({
    _start: String(start),
    _end: String(end),
    _sort: field,
    _order: order,
  });
  for (const [k, v] of Object.entries(params.filter ?? {})) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  return `/admin/${resource}?${qs.toString()}`;
}

function parseTotal(headers: Headers, fallback: number): number {
  const range = headers.get("Content-Range");
  if (!range) return fallback;
  const m = range.match(/\d+-\d+\/(\d+)/);
  return m ? Number(m[1]) : fallback;
}

export const dataProvider: DataProvider = {
  async getList(resource, params) {
    const { data, headers } = await request<unknown[]>(buildListQuery(resource, params));
    return { data: data as never[], total: parseTotal(headers, (data ?? []).length) };
  },
  async getOne(resource, params) {
    const { data } = await request<unknown>(`/admin/${resource}/${params.id}`);
    return { data: data as never };
  },
  async getMany(resource, params) {
    const ids = params.ids;
    const results = await Promise.all(
      ids.map((id) => request<unknown>(`/admin/${resource}/${id}`).then((r) => r.data)),
    );
    return { data: results as never[] };
  },
  async getManyReference(resource, params) {
    const filter = { ...params.filter, [params.target]: params.id };
    const listParams = { ...params, filter } as import("react-admin").GetListParams;
    const { data, headers } = await request<unknown[]>(buildListQuery(resource, listParams));
    return { data: data as never[], total: parseTotal(headers, (data ?? []).length) };
  },
  async create(resource, params) {
    const { data } = await request<unknown>(`/admin/${resource}`, { method: "POST", json: params.data });
    return { data: data as never };
  },
  async update(resource, params) {
    const { data } = await request<unknown>(`/admin/${resource}/${params.id}`, {
      method: "PUT",
      json: params.data,
    });
    return { data: data as never };
  },
  async updateMany(resource, params) {
    await Promise.all(
      params.ids.map((id) =>
        request<unknown>(`/admin/${resource}/${id}`, { method: "PUT", json: params.data }),
      ),
    );
    return { data: params.ids };
  },
  async delete(resource, params) {
    const { data } = await request<unknown>(`/admin/${resource}/${params.id}`, { method: "DELETE" });
    return { data: data as never };
  },
  async deleteMany(resource, params) {
    await Promise.all(
      params.ids.map((id) => request<unknown>(`/admin/${resource}/${id}`, { method: "DELETE" })),
    );
    return { data: params.ids };
  },
};

export async function adminApi<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const { data } = await request<T>(path, init);
  return data;
}
